import { Button } from "@msuite/ui/button";
import { useDropzone } from "@msuite/ui/file-upload";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Portal } from "@msuite/ui/portal";
import { Spinner } from "@msuite/ui/spinner";
import { cn } from "@msuite/ui/utils";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { submitDroppedFiles, useUpload } from "@/api/files";
import { useDropzoneBatches } from "@/hooks/use-dropzone-batches";
import { DropzoneBatchModal } from "@/modals/files/dropzone-batch-modal";
import { DROPZONE_SPRING } from "./dropzone-spring";
import { GeneralDropzoneProgress } from "./general-dropzone-progress";
import { GeneralDropzoneTarget } from "./general-dropzone-target";

/**
 * How long the settled pill stands before it closes itself.
 *
 * Long enough to read a sentence, notice a figure that is not the one expected, and reach the
 * link — which is what the pill is for once the transfer is over, and what a beat of a second and
 * a half was not enough for. It is also the whole of the window: the report behind the link is
 * reachable from nowhere else, so a pill that closed while somebody was still looking at it would
 * have taken the only account of the drop with it.
 */
const SETTLED_DURATION = 10_000;

/**
 * The pill's width while it is only announcing itself, and while a drag hangs over it. Two fixed
 * figures rather than a width the content decides, because the pill has to grow sideways as well
 * as down — an area is what a drop lands in, and a box the width of a sentence does not read as
 * one. Fixed also means the animation has somewhere to go: from the content's own width there is
 * nothing to interpolate between, and the longest translation would set the resting size.
 */
const RESTING_WIDTH = 352;
const ARMED_WIDTH = 432;

/**
 * The application's drop target for files dragged in from outside the browser. It is invisible
 * until a drag starts anywhere on the document, rises into the bottom of the window while one is
 * in flight, and stays well past the end of the upload: the pill is the only place the transfer is
 * reported, so it has to outlive both the drag that started it and the upload itself. A transfer
 * that vanished the instant it finished would leave nobody sure it had.
 *
 * A drop is two legs and the pill shows both. The bytes go to storage over a plain `fetch` that
 * reports no progress, so that leg is a spinner and a count. The server then records each file and
 * puts it in the queue to be filed, and those answers arrive over the sync path as the worker
 * writes them — so the meter is a count of files that have actually been settled, not of bytes.
 *
 * **Every outcome gets the same closing window, and it is ten seconds.** However the drop went,
 * the pill states it and then goes — there is no state it holds open indefinitely, because a
 * permanent box at the bottom of the window stops being read. What it offers within those ten
 * seconds is the report, which is the whole account of the drop and is reachable from nowhere
 * else: the inbox lists only what was left over, and a file that *was* filed correctly has no page
 * anywhere that says so.
 *
 * The clock is paused while the pointer is on the pill and while the report is open, so it only
 * ever expires on a drop nobody is looking at. That is the difference between a notice that closes
 * itself and one that closes on you.
 */
export function GeneralDropzone() {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { uploadFn } = useUpload();
	const reportModal = useDisclosure();
	const dropzone = useDropzone({
		onDrop: handleOnDrop,
		// The pill is reachable only mid-drag or mid-upload, so a click on it would be a file
		// dialog nobody asked for. While an upload runs it is the progress report, not a control.
		noClick: true,
		noKeyboard: true,
	});

	/** State */
	// The batches this person is watching, and how many files are still on their way to storage.
	// A count rather than a flag, because a second drop landing mid-upload is a second request and
	// a boolean would be cleared by whichever of them finished first.
	const [batchIds, setBatchIds] = useState<string[]>([]);
	const [sendingCount, setSendingCount] = useState(0);
	// Whether the pointer is resting on the pill, which holds the closing clock. A reader who has
	// moved to the pill is mid-sentence, and the ten seconds are measured from when they leave.
	const [isHovered, setIsHovered] = useState(false);

	/** Sync */
	const batches = useDropzoneBatches(batchIds);

	/** Computed */
	const isSending = sendingCount > 0;
	const isSettled = batches.total > 0 && batches.settled === batches.total;
	const problems = batches.unplaced + batches.failed;
	const isVisible = dropzone.isDragGlobal || isSending || batchIds.length > 0;
	const isArmed = dropzone.isDragAccept;
	const progress = batches.total ? (100 * batches.settled) / batches.total : 0;

	/** Effects */
	useEffect(() => {
		// The closing window, on every outcome rather than only the clean one. The three conditions
		// that hold it open are all "somebody is still using this": an upload is still running, the
		// pointer is on the pill, or the report is open — and the last is load-bearing, because
		// closing means clearing `batchIds`, which is the set the open report is a report of.
		if (isSending || !isSettled || isHovered || reportModal.isOpen) return;
		const timeout = setTimeout(() => setBatchIds([]), SETTLED_DURATION);
		return () => clearTimeout(timeout);
	}, [isSending, isSettled, isHovered, reportModal.isOpen]);

	useEffect(() => {
		// A pill that goes while the pointer is over it never gets its `mouseleave`, and the flag
		// would still be set when the next drop settles — holding the clock open for ever on a pill
		// nobody is near. The hidden pill is under no pointer by definition, so this says so.
		if (!isVisible) setIsHovered(false);
	}, [isVisible]);

	/** Functions */
	async function handleOnDrop(acceptedFiles: File[]) {
		if (acceptedFiles.length === 0) return;

		// A drop landing on the summary starts the pill over: the batch it would have joined has
		// already been reported, and its unplaced files are in the inbox rather than here.
		if (isSettled) setBatchIds([]);

		setSendingCount((count) => count + acceptedFiles.length);
		try {
			const body = new FormData();
			for (const file of acceptedFiles) body.append("files", file);
			const results = await uploadFn(body);
			if (!results?.length) return;

			const batchId = await submitDroppedFiles(results);
			if (!batchId) return;
			setBatchIds((current) => (current.includes(batchId) ? current : [...current, batchId]));
		} finally {
			setSendingCount((count) => count - acceptedFiles.length);
		}
	}

	/** Computed */
	const label = isSending
		? t("file_upload.n_files_uploading", { count: `${sendingCount}` })
		: isSettled && problems > 0
			? t("file_upload.n_files_could_not_be_sorted", { count: `${problems}` })
			: isSettled
				? t("file_upload.n_files_sorted", { count: `${batches.total}` })
				: batches.total > 0
					? t("file_upload.sorting_n_files", { count: `${batches.total}` })
					: t("file_upload.drop_files_to_sort");

	/** Render */
	return (
		<Portal>
			{/* Fixed rather than absolute: portalled onto the body, `absolute` resolves against the
			    document and drifts with the page's scroll. Above the modal layer at z-500 so a drag
			    still finds it while a dialog stands open, and the lane itself takes no pointer
			    events so it never swallows a click across the bottom of the screen. */}
			<div className="pointer-events-none fixed inset-x-0 bottom-8 z-520 flex justify-center">
				<AnimatePresence>
					{isVisible && (
						<motion.div
							className="pointer-events-auto"
							onMouseEnter={() => setIsHovered(true)}
							onMouseLeave={() => setIsHovered(false)}
							initial={{ y: 32, opacity: 0, scale: 0.94 }}
							animate={{ y: 0, opacity: 1, scale: 1 }}
							exit={{ y: 24, opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
							transition={DROPZONE_SPRING}
						>
							{/* The drop handlers sit on a plain wrapper rather than on the pill, so the
							    box the browser hit-tests a drag against is the one the pill is growing
							    to rather than the one it has reached. A release near the edge of the
							    rectangle lands even while the pill is still on its way there. */}
							<div {...dropzone.getRootProps()}>
								<motion.div
									// Width alone, and each row below collapses its own height: one
									// animation per axis, each owned by the element that knows its
									// size. The pill's box is never animated as a whole, so its corners
									// and its text stay undistorted.
									initial={false}
									animate={{ width: isArmed ? ARMED_WIDTH : RESTING_WIDTH }}
									transition={DROPZONE_SPRING}
									className={cn(
										"flex flex-col overflow-hidden rounded-2xl border p-3 shadow-lg",
										"transition-colors duration-200",
										isArmed
											? // Accent and white in both colour schemes: an armed target
												// says where the files are about to land, and that is not
												// a decision the theme gets to make.
												"border-accent-400 bg-accent-600 **:text-white!"
											: "card-bg border-color text-color",
									)}
								>
									<div className="h-stack justify-center gap-2">
										{isSending || (batches.total > 0 && !isSettled) ? (
											<Spinner color={isArmed ? "white" : "primary"} />
										) : (
											<Icon
												name={
													isSettled && problems > 0
														? "TriangleAlert"
														: isSettled
															? "CircleCheck"
															: "Files"
												}
												size={15}
												className={cn(
													isSettled && problems > 0 && "stroke-amber-500",
													isSettled && problems === 0 && "stroke-emerald-500",
												)}
											/>
										)}
										<p className="text-center font-medium text-sm">{label}</p>
									</div>
									{/* Held through the success beat rather than collapsed the moment the
									    last file settles, so the bar is seen full. Torn down at 99% it
									    would read as a transfer that stopped short. */}
									<AnimatePresence initial={false}>
										{batches.total > 0 && <GeneralDropzoneProgress value={progress} />}
									</AnimatePresence>
									{/* The way out of the pill, and the only one there is. Offered on every
									    settled drop rather than only a troubled one: the report is the whole
									    account of what happened, and a file that was filed correctly is
									    recorded on no page anywhere else. */}
									<AnimatePresence initial={false}>
										{isSettled && (
											<motion.div
												initial={{ height: 0, opacity: 0 }}
												animate={{ height: "auto", opacity: 1 }}
												exit={{ height: 0, opacity: 0 }}
												transition={DROPZONE_SPRING}
												className="overflow-hidden"
											>
												<div className="h-stack justify-center pt-3">
													<Button
														size="xs"
														variant="ghost"
														icon={<Icon name="ListChecks" />}
														onClick={reportModal.onOpen}
													>
														{t("file_upload.show_details")}
													</Button>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
									{/* Still offered mid-upload: a running transfer is no reason to
									    refuse the next batch, and the pill is the only place to put it. */}
									<AnimatePresence initial={false}>
										{isArmed && <GeneralDropzoneTarget />}
									</AnimatePresence>
								</motion.div>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
			<input {...dropzone.getInputProps()} />
			{/* Outside the pill's own subtree, so the exit animation cannot unmount the report
			    mid-read. The closing clock is held while it is open for the other half of that:
			    clearing `batchIds` would leave the modal asking about batches nothing is watching. */}
			{reportModal.isOpen && (
				<DropzoneBatchModal
					isOpen={reportModal.isOpen}
					onClose={reportModal.onClose}
					batchIds={batchIds}
				/>
			)}
		</Portal>
	);
}
